import React, { Component } from 'react'
import { connect } from 'react-redux'
import { withRouter } from 'react-router-dom'
import InvoiceItem from './InvoiceItem'
import InvoiceForm from './InvoiceForm'
import { InvoiceService } from '../../../services'
import { startFetch, endFetch } from '../../../actions/generalActions'
import ToastService from '../shared/Toast/ToastService'
import { deepestCategoryId, toUnitPrice } from './invoiceProducts'

class InvoiceItemContainer extends Component {
  state = {
    invoice: null,
    products: [],
    saleTypes: [],
    headerErrors: {},
    fetchError: null,
  }

  componentDidMount() {
    this.fetchInvoice(this.props)
    this.fetchSaleTypes()
  }

  // The header's "Tipo de venda" list comes from /invoices/get_sale_types, the
  // same source UserContainer uses for the creation form — it is not part of
  // the invoice detail payload. Fetched once so its identity stays stable
  // across the refetches triggered by every product operation.
  fetchSaleTypes = () => {
    const { campaign, client } = this.props
    InvoiceService.getSaleTypes({ clientId: client.slug, campaignId: campaign.slug })
      .then(response => this.setState({ saleTypes: response.data.sale_types || [] }))
      .catch(() => {})
  }

  componentDidUpdate(prevProps) {
    if (prevProps.match.params.id !== this.props.match.params.id) {
      this.fetchInvoice(this.props)
    }
  }

  fetchInvoice = ({ campaign, client, match }) => {
    this.props.startFetch()
    InvoiceService.getInvoiceDetail({
      clientId: client.slug,
      campaignId: campaign.slug,
      id: match.params.id,
    })
      .then(response => {
        // The endpoint returns the invoice under `invoice`, with its products
        // as invoice_details under `invoice.details`.
        const invoice = response.data.invoice
        this.setState({ invoice, products: (invoice && invoice.details) || [], fetchError: null })
        this.props.endFetch()
      })
      .catch(error => {
        this.props.endFetch()
        this.setState({ fetchError: error || new Error('Failed to load invoice') })
      })
  }

  handleProductCreation = (productData) => {
    const { campaign, client } = this.props
    const { invoice } = this.state
    const { selectedProduct, quantity, unitPrice } = productData

    this.props.startFetch()
    InvoiceService.addInvoiceProduct({
      clientId: client.slug,
      campaignId: campaign.slug,
      id: invoice.id,
      productId: selectedProduct.id,
      clientCategoryId: deepestCategoryId(productData),
      quantity: quantity,
      unitPrice: toUnitPrice(unitPrice),
    })
      .then(() => {
        this.fetchInvoice(this.props)
        this.props.endFetch()
      })
      .catch(() => {
        this.props.endFetch()
        ToastService.error('Erro ao adicionar o produto')
      })
  }

  handleProductEdit = (productData, oldProduct) => {
    const { campaign, client } = this.props
    const { invoice } = this.state
    const { selectedProduct, quantity, unitPrice } = productData

    this.props.startFetch()
    InvoiceService.editInvoiceProduct({
      clientId: client.slug,
      campaignId: campaign.slug,
      invoiceId: invoice.id,
      productDetailId: oldProduct.id,
      productId: selectedProduct.id,
      clientCategoryId: deepestCategoryId(productData),
      quantity: quantity,
      unitPrice: toUnitPrice(unitPrice),
    })
      .then(() => {
        this.fetchInvoice(this.props)
        this.props.endFetch()
      })
      .catch(() => {
        this.props.endFetch()
        ToastService.error('Erro ao editar o produto')
      })
  }

  handleProductRemove = (product) => {
    const { campaign, client } = this.props
    const { invoice } = this.state

    this.props.startFetch()
    InvoiceService.removeInvoiceProduct({
      clientId: client.slug,
      campaignId: campaign.slug,
      id: invoice.id,
      productDetailId: product.id,
    })
      .then(() => {
        this.fetchInvoice(this.props)
        this.props.endFetch()
      })
      .catch(() => {
        this.props.endFetch()
        ToastService.error('Erro ao remover o produto')
      })
  }

  saveHeader = ({ number, invoiceDate, saleType, document }) => {
    const { campaign, client } = this.props
    const { invoice } = this.state

    return InvoiceService.editInvoiceHeader({
      clientId: client.slug,
      campaignId: campaign.slug,
      invoiceId: invoice.id,
      number,
      invoiceDate,
      saleType,
      document: document || {},
      name: document && document.name,
    })
      .then(() => this.setState({ headerErrors: {} }))
  }

  reportHeaderError = (error) => {
    if (error && error.response && error.response.data) {
      this.setState({ headerErrors: error.response.data })
    } else {
      ToastService.error('Erro ao guardar a fatura')
    }
  }

  sendToValidation = () => {
    const { campaign, client } = this.props
    const { invoice } = this.state

    return InvoiceService.sendToValidation({
      clientId: client.slug,
      campaignId: campaign.slug,
      id: invoice.id,
    })
  }

  // "Guardar rascunho": header PUT only, the invoice stays in the draft list.
  handleHeaderEdit = (headerFields) => {
    this.props.startFetch()
    this.saveHeader(headerFields)
      .then(() => {
        this.fetchInvoice(this.props)
        this.props.endFetch()
      })
      .catch(error => {
        this.props.endFetch()
        this.reportHeaderError(error)
      })
  }

  // "Enviar fatura": the header edits still open in the form are saved first,
  // because set_invoice_to_approval makes the invoice read-only and they could
  // no longer be applied afterwards. A rejected header save aborts the send.
  handleSaveAndSubmit = (headerFields) => {
    this.props.startFetch()
    this.saveHeader(headerFields)
      .then(
        () => this.sendToValidation()
          .then(() => {
            this.props.endFetch()
            this.props.history.push('/invoice/to_approve')
          })
          .catch(() => {
            this.props.endFetch()
            ToastService.error('Erro ao enviar a fatura para aprovação')
          }),
        error => {
          this.props.endFetch()
          this.reportHeaderError(error)
        }
      )
  }

  // The draft page is editable by route, so after a successful send the user
  // is moved off it, as in DefaultTemplate — otherwise an invoice already at
  // approval would still be presented as a draft.
  handleSubmitToValidation = () => {
    this.props.startFetch()
    this.sendToValidation()
      .then(() => {
        this.props.endFetch()
        this.props.history.push('/invoice/to_approve')
      })
      .catch(() => {
        this.props.endFetch()
        ToastService.error('Erro ao enviar a fatura para aprovação')
      })
  }

  render() {
    const { campaign, client, linking, editable } = this.props
    const { invoice, products, saleTypes, headerErrors, fetchError } = this.state

    // Async errors (from Promise callbacks) can't be caught by React error boundaries
    // directly — they happen outside React's call stack. Throwing synchronously from
    // render() re-enters React's call stack, so the nearest ErrorBoundary catches it.
    if (fetchError) throw fetchError

    if (!invoice) return null

    // The route decides editability, as in DefaultTemplate: only the
    // /invoice/created tab passes `editable`, and it lists drafts only. The
    // detail payload's `status` is not reliable enough to gate on — it does
    // not always carry the literal 'created'.
    if (editable) {
      return (
        <InvoiceForm
          campaign={campaign}
          client={client}
          invoice={invoice}
          saleTypes={saleTypes}
          errors={headerErrors}
          serverProducts={products}
          onProductAdd={this.handleProductCreation}
          onProductEdit={this.handleProductEdit}
          onProductRemove={this.handleProductRemove}
          onSaveDraft={this.handleHeaderEdit}
          onSubmitFatura={this.handleSaveAndSubmit}
        />
      )
    }

    return (
      <InvoiceItem
        campaign={campaign}
        linking={linking}
        editable={false}
        invoice={invoice}
        products={products}
        headerErrors={headerErrors}
        saleTypes={saleTypes}
        onProductCreation={this.handleProductCreation}
        onProductEdit={this.handleProductEdit}
        onProductRemove={this.handleProductRemove}
        onHeaderEdit={this.handleHeaderEdit}
        onSubmitToValidation={this.handleSubmitToValidation}
      />
    )
  }
}

const mapDispatchToProps = (dispatch) => ({
  startFetch: () => dispatch(startFetch()),
  endFetch: () => dispatch(endFetch()),
})

export default withRouter(connect(null, mapDispatchToProps)(InvoiceItemContainer))
